import { useCallback } from 'react';
import { toast } from 'sonner';
import { StoreCore } from '../types/store';
import { FamilyEvent } from '../types/family';
import { PollKind, SchoolKind } from '../types/shared';
import { uid, withFamily } from '../utils/db';

export function useSharedActions(core: StoreCore) {
  const { setDb, family, viewer, isAdmin } = core;
  const fid = family.id;

  // ---------- Nakupovalni seznam ----------
  const addShopping = useCallback(
    (text: string) =>
      setDb((p) => ({ ...p, shopping: [...p.shopping, { id: uid('sh'), familyId: fid, text, done: false, addedBy: viewer.id }] })),
    [setDb, fid, viewer.id]
  );
  const toggleShopping = useCallback(
    (id: string) => setDb((p) => ({ ...p, shopping: p.shopping.map((s) => (s.id === id ? { ...s, done: !s.done } : s)) })),
    [setDb]
  );
  const clearBought = useCallback(
    () => setDb((p) => ({ ...p, shopping: p.shopping.filter((s) => !(s.familyId === fid && s.done)) })),
    [setDb, fid]
  );

  // ---------- Glasovanja ----------
  const createPoll = useCallback(
    (title: string, kind: PollKind, options: string[]) =>
      setDb((p) => ({
        ...p,
        polls: [
          {
            id: uid('p'),
            familyId: fid,
            title,
            kind,
            open: true,
            createdBy: viewer.id,
            options: options.map((label) => ({ id: uid('o'), label, votes: [] })),
          },
          ...p.polls,
        ],
      })),
    [setDb, fid, viewer.id]
  );
  const vote = useCallback(
    (pollId: string, optionId: string) =>
      setDb((p) => ({
        ...p,
        polls: p.polls.map((poll) =>
          poll.id !== pollId || !poll.open
            ? poll
            : {
                ...poll,
                options: poll.options.map((o) => ({
                  ...o,
                  votes: o.id === optionId ? Array.from(new Set([...o.votes, viewer.id])) : o.votes.filter((v) => v !== viewer.id),
                })),
              }
        ),
      })),
    [setDb, viewer.id]
  );
  const addPollOption = useCallback(
    (pollId: string, label: string) =>
      setDb((p) => ({
        ...p,
        polls: p.polls.map((poll) => (poll.id === pollId ? { ...poll, options: [...poll.options, { id: uid('o'), label, votes: [] }] } : poll)),
      })),
    [setDb]
  );
  const closePoll = useCallback(
    (pollId: string) => setDb((p) => ({ ...p, polls: p.polls.map((poll) => (poll.id === pollId ? { ...poll, open: false } : poll)) })),
    [setDb]
  );

  // ---------- Želje za izlete ----------
  const addWish = useCallback(
    (title: string, note?: string, link?: string) =>
      setDb((p) => ({ ...p, wishes: [...p.wishes, { id: uid('w'), familyId: fid, title, note, link, addedBy: viewer.id }] })),
    [setDb, fid, viewer.id]
  );
  const removeWish = useCallback(
    (id: string) =>
      setDb((p) => ({ ...p, wishes: p.wishes.filter((w) => !(w.id === id && (isAdmin || w.addedBy === viewer.id))) })),
    [setDb, isAdmin, viewer.id]
  );

  // ---------- Koledar ----------
  const addEvent = useCallback(
    (ev: Omit<FamilyEvent, 'id' | 'source' | 'createdBy'>) => {
      setDb((p) => withFamily(p, fid, (f) => ({ ...f, events: [...f.events, { ...ev, id: uid('e'), source: 'app', createdBy: viewer.id }] })));
      toast.success('Dogodek dodan v družinski koledar.');
    },
    [setDb, fid, viewer.id]
  );
  const setEventTransport = useCallback(
    (eventId: string, patch: { driverId?: string; pickupId?: string }) =>
      setDb((p) => withFamily(p, fid, (f) => ({ ...f, events: f.events.map((e) => (e.id === eventId ? { ...e, ...patch } : e)) }))),
    [setDb, fid]
  );
  const deleteEvent = useCallback(
    (eventId: string) =>
      setDb((p) =>
        withFamily(p, fid, (f) => ({
          ...f,
          events: f.events.filter((e) => !(e.id === eventId && e.source === 'app' && (isAdmin || e.createdBy === viewer.id))),
        }))
      ),
    [setDb, fid, isAdmin, viewer.id]
  );

  // ---------- Šola ----------
  const addSchoolItem = useCallback(
    (input: { memberId: string; kind: SchoolKind; title: string; date: string; blocks: { date: string; minutes: number }[] }) => {
      if (!isAdmin && input.memberId !== viewer.id) return;
      setDb((p) => ({
        ...p,
        schoolItems: [
          ...p.schoolItems,
          {
            id: uid('si'),
            familyId: fid,
            memberId: input.memberId,
            kind: input.kind,
            title: input.title,
            date: input.date,
            done: false,
            createdBy: viewer.id,
            blocks: input.blocks.map((b) => ({ ...b, id: uid('b'), done: false })),
          },
        ],
      }));
      toast.success('Shranjeno v šolske obveznosti.');
    },
    [isAdmin, viewer.id, setDb, fid]
  );
  const canEditSchool = useCallback(
    (memberId: string, createdBy: string) => isAdmin || (memberId === viewer.id && createdBy === viewer.id),
    [isAdmin, viewer.id]
  );
  const toggleSchoolItem = useCallback(
    (id: string) =>
      setDb((p) => ({
        ...p,
        schoolItems: p.schoolItems.map((s) => (s.id === id && (isAdmin || s.memberId === viewer.id) ? { ...s, done: !s.done } : s)),
      })),
    [setDb, isAdmin, viewer.id]
  );
  const toggleBlock = useCallback(
    (itemId: string, blockId: string) =>
      setDb((p) => ({
        ...p,
        schoolItems: p.schoolItems.map((s) =>
          s.id === itemId && (isAdmin || s.memberId === viewer.id)
            ? { ...s, blocks: s.blocks.map((b) => (b.id === blockId ? { ...b, done: !b.done } : b)) }
            : s
        ),
      })),
    [setDb, isAdmin, viewer.id]
  );
  const addBlock = useCallback(
    (itemId: string, date: string, minutes: number) =>
      setDb((p) => ({
        ...p,
        schoolItems: p.schoolItems.map((s) =>
          s.id === itemId && (isAdmin || s.memberId === viewer.id)
            ? { ...s, blocks: [...s.blocks, { id: uid('b'), date, minutes, done: false }].sort((a, b) => a.date.localeCompare(b.date)) }
            : s
        ),
      })),
    [setDb, isAdmin, viewer.id]
  );
  const deleteSchoolItem = useCallback(
    (id: string) =>
      setDb((p) => ({
        ...p,
        schoolItems: p.schoolItems.filter((s) => !(s.id === id && (isAdmin || (s.memberId === viewer.id && s.createdBy === viewer.id)))),
      })),
    [setDb, isAdmin, viewer.id]
  );

  return {
    addShopping,
    toggleShopping,
    clearBought,
    createPoll,
    vote,
    addPollOption,
    closePoll,
    addWish,
    removeWish,
    addEvent,
    setEventTransport,
    deleteEvent,
    addSchoolItem,
    canEditSchool,
    toggleSchoolItem,
    toggleBlock,
    addBlock,
    deleteSchoolItem,
  };
}
